// Field users must allow location to use the app at all (owner's rule, 2026-10-07): a punch
// without a location is a punch the office cannot defend. The phone owns the permission, so
// the app asks once, and when it has been refused it stops here and shows how to turn it on,
// re-checking whenever the person comes back from Settings. Office roles are not gated: a
// desk has no GPS.
import { useEffect, useState } from 'react';
import { isField } from '@tradeview/shared';
import { Icon } from './Icon';
import type { Session } from '../auth';

type State = 'checking' | 'ok' | 'ask' | 'denied' | 'off' | 'nofix';

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent);
const standalone = () => window.matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

function probe(timeoutMs = 12000): Promise<State> {
  return new Promise(resolve => {
    if (!('geolocation' in navigator)) return resolve('off');
    navigator.geolocation.getCurrentPosition(
      () => resolve('ok'),
      err => resolve(err.code === 1 ? 'denied' : err.code === 2 ? 'off' : 'nofix'),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 5 * 60_000 });
  });
}

export function useLocationGate(session: Session | null) {
  const gated = !!session && isField(session.role);
  const [state, setState] = useState<State>(gated ? 'checking' : 'ok');
  useEffect(() => {
    if (!gated) { setState('ok'); return; }
    let alive = true;
    let perm: PermissionStatus | null = null;
    const check = async () => {
      try {
        perm = await navigator.permissions.query({ name: 'geolocation' });
        if (!alive) return;
        if (perm.state === 'granted') { setState(await probe()); return; }
        if (perm.state === 'denied') { setState('denied'); return; }
        setState('ask');
        perm.onchange = () => { if (perm?.state === 'granted') void probe().then(s => alive && setState(s)); else if (perm?.state === 'denied') setState('denied'); };
      } catch { setState('ask'); }   // older Safari: no permissions API; the tap below asks
    };
    void check();
    const vis = () => { if (!document.hidden) void check(); };   // back from Settings
    document.addEventListener('visibilitychange', vis);
    return () => { alive = false; document.removeEventListener('visibilitychange', vis); if (perm) perm.onchange = null; };
  }, [gated]);
  const ask = async () => { setState('checking'); setState(await probe()); };
  return { state, ask, gated };
}

export function LocationGate({ state, onAsk }: { state: Exclude<State, 'ok'>; onAsk: () => void }) {
  const ios = isIOS();
  return (
    <div className="min-h-full grid place-items-center px-4 py-8">
      <div className="w-full max-w-[440px] flex flex-col gap-3">
        <div className="w-16 h-16 rounded-[18px] bg-accent text-accent-ink grid place-items-center mx-auto"><Icon name="map" size={32} /></div>
        {state === 'checking' && <><b className="font-disp font-bold text-[26px] text-center block">Checking location…</b><div className="skel h-3 w-32 mx-auto" /></>}
        {state === 'ask' && (
          <>
            <b className="font-disp font-bold text-[26px] text-center block">Allow location to use TradeView</b>
            <p className="m-0 text-muted text-[14px] text-center">Every punch and every record is stamped with where the phone was. The app asks once. Location is read when you tap, and every few minutes while you are on the clock with the app open. Never off the clock.</p>
            <button className="btn btn-primary btn-block" onClick={onAsk}>Allow location</button>
            <p className="m-0 text-muted text-[12.5px] text-center">When the phone asks, choose <b className="text-fg">Allow While Using App</b>.</p>
          </>
        )}
        {(state === 'denied' || state === 'off') && (
          <>
            <b className="font-disp font-bold text-[26px] text-center block">{state === 'off' ? 'Location Services are off' : 'Location is turned off for TradeView'}</b>
            <p className="m-0 text-muted text-[14px] text-center">TradeView needs location to run. Turn it on, come back, and this screen goes away by itself.</p>
            <div className="card text-[13.5px]">
              {ios ? (
                <ol className="m-0 pl-5 flex flex-col gap-1">
                  <li>Open the iPhone <b>Settings</b> app.</li>
                  {state === 'off' && <li><b>Privacy &amp; Security</b> → <b>Location Services</b> → turn it <b>on</b>.</li>}
                  <li>Scroll to <b>{standalone() ? 'TradeView' : 'Safari'}</b> → <b>Location</b>.</li>
                  <li>Choose <b>While Using the App</b>{standalone() ? '' : ', then open TradeView from the home screen icon'}.</li>
                </ol>
              ) : (
                <ol className="m-0 pl-5 flex flex-col gap-1">
                  {state === 'off' && <li>Pull down from the top of the screen and turn <b>Location</b> on.</li>}
                  <li>Open <b>Settings</b> → <b>Apps</b> → <b>Chrome</b> (or <b>TradeView</b> if installed) → <b>Permissions</b> → <b>Location</b>.</li>
                  <li>Choose <b>Allow only while using the app</b>.</li>
                  <li>Come back here. If it still shows, in Chrome tap the lock icon by the address → <b>Permissions</b> → <b>Location</b> → <b>Allow</b>.</li>
                </ol>
              )}
            </div>
            <button className="btn btn-block" onClick={onAsk}>I turned it on</button>
          </>
        )}
        {state === 'nofix' && (
          <>
            <b className="font-disp font-bold text-[26px] text-center block">No location fix yet</b>
            <p className="m-0 text-muted text-[14px] text-center">Location is allowed but the phone could not get a position. Step outside or near a window and try again.</p>
            <button className="btn btn-primary btn-block" onClick={onAsk}>Try again</button>
          </>
        )}
        <p className="m-0 text-muted text-[12px] text-center">Need help? Text the office.</p>
      </div>
    </div>
  );
}
